'use client'

import React, { useCallback, useState } from 'react'

import { useDragToPan } from '../../hooks/useDragToPan'
import { useImageZoom } from '../../hooks/useImageZoom'

import type { ColorScheme } from '../../types'
import { FileThumbnail } from '../FileThumbnail/FileThumbnail'
import { getExtension } from '../FileThumbnail/fileTypeIcon'
import { Modal } from '../Modal/Modal'
import { FileViewerSize, fileViewerModalBodyStyles } from './FileViewer.styles'
import { FileViewerControls } from './FileViewerControls'
import { ZoomableImage } from './ZoomableImage'

export type FileViewerFile = {
  src: string
  name: string
  /** Drives the image / pdf / fallback branch. Absent → fallback card. */
  mimeType?: string
}

export type FileViewerProps = {
  isOpen: boolean
  onClose: () => void
  file: FileViewerFile
  /**
   * Modal portals to document.body, escaping any DarkProvider subtree — so the
   * scheme must arrive explicitly.
   */
  colorScheme?: ColorScheme
  /**
   * Escape hatch: render this node in the body instead of the built-in
   * branches. Lets the web-app keep video preview (react-player) without the
   * dark package gaining the dependency.
   */
  mediaOverride?: React.ReactNode
  /** Download affordance on the fallback (non-previewable) card. Omit to hide. */
  onDownload?: () => void
  isDownloading?: boolean
  /** Sits above the web-app's Chakra v1 incident drawer by default. */
  zIndex?: number
}

/**
 * File lightbox built on the package Modal (ENG-32107): images get zoom /
 * drag-pan / click-to-fullscreen, PDFs render embedded, anything else falls
 * back to a FileThumbnail download card. Composing Modal keeps the rebrand
 * chrome — the cat-ear close, the clip-path silhouette, and its overlay
 * machinery (escape stack, focus trap, data-no-focus-lock) — so the viewer
 * matches every other dark surface; the controls render through Modal's
 * `header` slot, directly under the title. "Full" is Modal's maxW=100vw
 * treatment (capped by its 90vh height), the same behavior the legacy viewer
 * shipped.
 */
export const FileViewer = ({
  isOpen,
  onClose,
  file,
  colorScheme = 'dark',
  mediaOverride,
  onDownload,
  isDownloading,
  zIndex = 1500,
}: FileViewerProps) => {
  const [size, setSize] = useState<FileViewerSize>('normal')

  const mimeType = file.mimeType ?? ''
  const isImage = mimeType.includes('image')
  const isPdf = mimeType.includes('pdf')
  const isFull = size === 'full'

  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    isZoomed,
  } = useImageZoom(isOpen)
  const { containerRef, isDragging, handlers } = useDragToPan(isZoomed)

  const handleToggleSize = useCallback(() => {
    setSize((prev) => {
      const next = prev === 'normal' ? 'full' : 'normal'
      if (next === 'normal') {
        resetZoom()
      }
      return next
    })
  }, [resetZoom])

  const renderMedia = () => {
    if (mediaOverride) return mediaOverride

    if (isImage) {
      return (
        <ZoomableImage
          colorScheme={colorScheme}
          containerRef={containerRef}
          isDragging={isDragging}
          name={file.name}
          onToggleSize={handleToggleSize}
          size={size}
          src={file.src}
          zoomLevel={zoomLevel}
          {...handlers}
        />
      )
    }

    if (isPdf) {
      return (
        <embed
          height='100%'
          src={file.src}
          style={{ height: '100%', minHeight: '400px', width: '100%' }}
          type='application/pdf'
          width='100%'
        />
      )
    }

    // Non-previewable formats (doc, csv, mp3, … — ENG-32108) are offered as a
    // download instead of an empty frame.
    return (
      <FileThumbnail
        colorScheme={colorScheme}
        extension={getExtension(file.name) || undefined}
        isDownloading={isDownloading}
        name={file.name}
        onDownload={onDownload}
      />
    )
  }

  return (
    <Modal
      bodyStyleOverrides={fileViewerModalBodyStyles(size)}
      colorScheme={colorScheme}
      header={
        <FileViewerControls
          colorScheme={colorScheme}
          onToggleSize={handleToggleSize}
          size={size}
          zoomActions={{
            onReset: resetZoom,
            onZoomIn: zoomIn,
            onZoomOut: zoomOut,
          }}
          zoomState={{
            canZoomIn,
            canZoomOut,
            isZoomed,
            // Same visibility rule as the legacy controls: zooming is only
            // offered on an image at full screen.
            shouldShowZoomControls: isImage && isFull,
          }}
        />
      }
      isOpen={isOpen}
      isSaveHidden
      maxW={isFull ? '100vw' : '900px'}
      onClose={onClose}
      title={file.name}
      zIndex={zIndex}
    >
      {renderMedia()}
    </Modal>
  )
}
